import React, { useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import { useCreateBundleComponent } from '~/hooks/apis/bundle/useBundles';
import { useGetOfferPackages } from '~/hooks/apis/offers/useGetOfferPackages';

import { BUNDLE_OCCUPANCIES, Bundle } from '~/types/bundles/bundle';

import FlashOfferPicker from './FlashOfferPicker';
import HotelAvailabilitySummary from './HotelAvailabilitySummary';
import { hotelPackageOptions } from './hotelPackages';
import useBundleTourSpan from './useBundleTourSpan';

interface Props {
  bundle: Bundle;
  open: boolean;
  onClose: () => void;
}

// Days are shown from 1, the departure day; the bundle stores them from 0.
const DAY_PATTERN = /^[1-9]\d*$/;

export default function HotelComponentDialog({ bundle, open, onClose }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [offer, setOffer] = useState<App.Offer | null>(null);
  const [packageId, setPackageId] = useState('');
  const [nights, setNights] = useState<number | ''>('');
  const [checkInDay, setCheckInDay] = useState('');

  const { endDay } = useBundleTourSpan(bundle);
  const { data: packages = [], isLoading: isLoadingPackages } = useGetOfferPackages(offer?.id_salesforce_external);
  const createComponent = useCreateBundleComponent(bundle.id);

  const packageOptions = useMemo(() => hotelPackageOptions(packages), [packages]);
  const selectedPackage = packageOptions.find((option) => option.packageId === packageId);
  const isValidDay = DAY_PATTERN.test(checkInDay);
  const dayOffset = Number(checkInDay) - 1;

  // A stay usually starts the day the tour ends.
  useEffect(() => {
    if (open && endDay !== undefined) {
      setCheckInDay(String(endDay + 1));
    }
  }, [open, endDay]);

  const handleOfferChange = (next: App.Offer | null) => {
    setOffer(next);
    setPackageId('');
    setNights('');
  };

  const handlePackageChange = (next: string) => {
    setPackageId(next);
    const options = packageOptions.find((option) => option.packageId === next)?.nightsOptions ?? [];
    setNights(options.length === 1 ? options[0] : '');
  };

  const handleClose = () => {
    handleOfferChange(null);
    createComponent.reset();
    onClose();
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await createComponent.mutateAsync({
        product_type: 'flashAccommodation',
        sequence_number: Math.max(-1, ...bundle.components.map((c) => c.sequence_number)) + 1,
        day_offset: dayOffset,
        offer_id: offer.id_salesforce_external,
        package_id: packageId,
        nights: Number(nights),
        occupancies: [...BUNDLE_OCCUPANCIES],
      });
      enqueueSnackbar(`Added ${offer.name}`, { variant: 'success' });
      handleClose();
    } catch {
      // The mutation error is shown in the dialog.
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogTitle>Add hotel</DialogTitle>
        <DialogContent>
          <Stack spacing={3}>
            <DialogContentText>
              Pick the flash hotel, the room package and how many nights, then the day of the bundle it checks in.
            </DialogContentText>

            <FlashOfferPicker value={offer} onChange={handleOfferChange} />

            {!!offer && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl fullWidth required disabled={isLoadingPackages}>
                  <InputLabel id="hotel-package-label">Room package</InputLabel>
                  <Select
                    labelId="hotel-package-label"
                    label="Room package"
                    value={packageId}
                    onChange={(event) => handlePackageChange(event.target.value)}
                  >
                    {packageOptions.map((option) => (
                      <MenuItem key={option.packageId} value={option.packageId} disabled={!option.nightsOptions.length}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl sx={{ minWidth: 140 }} required disabled={!selectedPackage}>
                  <InputLabel id="hotel-nights-label">Nights</InputLabel>
                  <Select
                    labelId="hotel-nights-label"
                    label="Nights"
                    value={nights}
                    onChange={(event) => setNights(Number(event.target.value))}
                  >
                    {(selectedPackage?.nightsOptions ?? []).map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Checks in on day"
                  type="number"
                  value={checkInDay}
                  onChange={(event) => setCheckInDay(event.target.value)}
                  error={checkInDay !== '' && !isValidDay}
                  helperText={
                    endDay !== undefined ? `The tour's last day is day ${endDay + 1}` : 'Day 1 is the departure day'
                  }
                  inputProps={{ min: 1, step: 1 }}
                  sx={{ minWidth: 180 }}
                  required
                />
              </Stack>
            )}

            {!!offer && !isLoadingPackages && packageOptions.length === 0 && (
              <Alert severity="warning">This offer has no room packages to bundle. Choose another hotel.</Alert>
            )}

            {!!offer && !!packageId && nights !== '' && isValidDay && (
              <HotelAvailabilitySummary
                stay={{ offerId: offer.id_salesforce_external, packageId, nights: Number(nights) }}
                dayOffset={dayOffset}
                departureDates={bundle.departure_dates}
              />
            )}

            {!!createComponent.error && <Alert severity="error">{createComponent.error.message}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!offer || !packageId || nights === '' || !isValidDay || createComponent.isPending}
          >
            Add hotel
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
